// src/pages/Students/StudentsList.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/common/Button';
import Pager from '../../components/common/Pager';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useStudents } from '../../hooks/useStudents'; // reusing your existing deleteStudent
import { useStudentsPage, useStudentYears } from '../../hooks/useStudentsPage';

const StudentList = () => {
  // Local UI state (filters + pagination)
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  // Debounce search to reduce query churn
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Reset to page 1 when filters/search/pageSize change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, paymentTypeFilter, yearFilter, pageSize]);

  // Distinct years for dropdown (server-side)
  const { years } = useStudentYears({ includeDeleted: false });

  // Server-side page query
  const {
    rows,
    count,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useStudentsPage({
    page: currentPage,
    pageSize,
    search: debouncedSearch,
    status: statusFilter,
    paymentType: paymentTypeFilter,
    year: yearFilter,
    includeDeleted: false,
  });

  // Keep your existing delete API; refetch the server-side list after success
  const { deleteStudent } = useStudents();

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this student?')) return;
    const result = await deleteStudent(id);
    if (result?.success) {
      alert('Student deleted successfully');
      // Clamp: if removing the last item on the last page, step back a page
      const nextTotal = Math.max(0, (count ?? 0) - 1);
      const totalPages = Math.max(1, Math.ceil(nextTotal / pageSize));
      setCurrentPage((p) => Math.min(p, totalPages));
      refetch();
    } else {
      alert(result?.error || 'Failed to delete student');
    }
  };

  const enrollmentTypeBadge = (value) =>
    value === 'Full' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800';

  const statusBadge = (value) =>
    value === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h1 className="text-xl font-semibold text-gray-900">Students Management</h1>
        <Link to="/students/new">
          <Button>
            <PlusIcon className="h-5 w-5 mr-1" />
            Add Student
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              type="text"
              id="search"
              placeholder="Search by Full Name, NRIC/Passport..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Status */}
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              id="status"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Non-Active">Non-Active</option>
            </select>
          </div>

          {/* Enrollment Type */}
          <div>
            <label htmlFor="paymentType" className="block text-sm font-medium text-gray-700 mb-1">
              Enrollment Type
            </label>
            <select
              id="paymentType"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={paymentTypeFilter}
              onChange={(e) => setPaymentTypeFilter(e.target.value)}
            >
              <option value="all">All Enrollment Types</option>
              <option value="Full">Full (10 subjects)</option>
              <option value="Individual">Individual</option>
            </select>
          </div>

          {/* Year */}
          <div>
            <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-1">
              Year
            </label>
            <select
              id="year"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
            >
              <option value="all">All Years</option>
              {years.map((y) => (
                <option key={y} value={String(y)}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {error ? (
          <div className="text-center py-8 text-red-500">Error: {String(error.message || error)}</div>
        ) : isLoading ? (
          <div className="text-center py-8">Loading students...</div>
        ) : count === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No students{debouncedSearch || statusFilter !== 'all' || paymentTypeFilter !== 'all' || yearFilter !== 'all' ? ' match your filters' : ' found'}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Full Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">NRIC/Passport</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact No.</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enrollment Year</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enrollment Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {rows.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className="px-3 py-1 whitespace-nowrap">
                        <Link
                          to={`/students/${row.id}`}
                          state={{ from: 'students' }}
                          className="text-sky-700 hover:text-sky-900 font-medium text-sm"
                          title={row.full_name}
                        >
                          {row.full_name}
                        </Link>
                      </td>
                      <td className="px-3 py-1 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{row.nric_passport || '-'}</div>
                      </td>
                      <td className="px-3 py-1 whitespace-nowrap">
                        <div className="text-sm text-gray-900 max-w-[260px] truncate" title={row.email}>
                          {row.email || '-'}
                        </div>
                      </td>
                      <td className="px-3 py-1 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{row.contact_no || '-'}</div>
                      </td>
                      <td className="px-3 py-1 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{row.enrollment_year ?? '-'}</div>
                      </td>
                      <td className="px-3 py-1 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${enrollmentTypeBadge(row.payment_type)}`}>
                          {row.payment_type}
                        </span>
                      </td>
                      <td className="px-3 py-1 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusBadge(row.status)}`}>
                          {row.status}
                        </span>
                      </td>
                      <td className="px-3 py-1 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex gap-1 justify-end">
                          <Link to={`/students/${row.id}`} state={{ from: 'students' }}>
                            <Button variant="outline" size="sm">View</Button>
                          </Link>
                          <Link to={`/students/${row.id}/edit`}>
                            <Button variant="outline" size="sm">Edit</Button>
                          </Link>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(row.id)}
                            className="text-red-600 hover:text-red-800"
                            disabled={isFetching}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer / Pagination — shared Pager */}
            <Pager
              pageSize={pageSize}
              setPageSize={setPageSize}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalRows={count}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default StudentList;
